import { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MAP_STYLE_URL } from 'common/constants';
import { colors } from 'styles/colors';

// Where the map opens when there is no point yet: Ramallah.
const DEFAULT_CENTER: [number, number] = [35.2034, 31.9038];

interface Props {
  latitude?: number | null;
  longitude?: number | null;
  // The pin can be dragged, and a click on the map moves it there - the way a
  // store sets its location in the mobile app. Only then does the map zoom: a
  // map that is only being looked at stays at the store.
  draggable?: boolean;
  onChange?: (latitude: number, longitude: number) => void;
  height?: number;
}

const hasPoint = (latitude?: number | null, longitude?: number | null) =>
  Number.isFinite(latitude) && Number.isFinite(longitude);

export default function LocationMap({ latitude, longitude, draggable = false, onChange, height = 320 }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null);
  const zoomControlRef = useRef<maplibregl.NavigationControl | null>(null);
  // Read by the map's handlers, which are attached once and would otherwise keep
  // the first render's callback.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // The map is built once; the point and the drag setting follow it below.
  useEffect(() => {
    if (!containerRef.current) return;

    const point = hasPoint(latitude, longitude);
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE_URL,
      center: point ? [longitude as number, latitude as number] : DEFAULT_CENTER,
      zoom: point ? 16 : 9,
    });
    zoomControlRef.current = new maplibregl.NavigationControl({ showCompass: false });

    const marker = new maplibregl.Marker({ color: colors.primary });
    marker.on('dragend', () => {
      const { lat, lng } = marker.getLngLat();
      onChangeRef.current?.(lat, lng);
    });
    if (point) marker.setLngLat([longitude as number, latitude as number]).addTo(map);

    map.on('click', (event) => {
      if (!marker.isDraggable()) return;
      marker.setLngLat(event.lngLat).addTo(map);
      onChangeRef.current?.(event.lngLat.lat, event.lngLat.lng);
    });

    mapRef.current = map;
    markerRef.current = marker;

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
      zoomControlRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const zoomControl = zoomControlRef.current;
    markerRef.current?.setDraggable(draggable);
    if (!map || !zoomControl) return;

    const zoomHandlers = [map.scrollZoom, map.boxZoom, map.doubleClickZoom, map.touchZoomRotate, map.keyboard];
    zoomHandlers.forEach((handler) => (draggable ? handler.enable() : handler.disable()));

    if (draggable && !map.hasControl(zoomControl)) map.addControl(zoomControl, 'top-right');
    if (!draggable && map.hasControl(zoomControl)) map.removeControl(zoomControl);
  }, [draggable]);

  // A point typed into the form moves the pin; one the pin itself set is
  // already where it is, and setting it again changes nothing.
  useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!map || !marker) return;

    if (!hasPoint(latitude, longitude)) {
      marker.remove();
      return;
    }

    const current = marker.getLngLat?.();
    if (current && current.lat === latitude && current.lng === longitude) return;

    marker.setLngLat([longitude as number, latitude as number]).addTo(map);
    map.easeTo({ center: [longitude as number, latitude as number] });
  }, [latitude, longitude]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height,
        border: `1px solid ${colors.border}`,
        borderRadius: '8px',
        overflow: 'hidden',
        cursor: draggable ? 'crosshair' : 'default',
      }}
    />
  );
}
